import React from "react";
import { Star, Quote, Award, Briefcase, Smile, Users } from "lucide-react";
import { enhancedTestimonials } from "../utils/mockData";
import useIntersectionObserver from "../hooks/useIntersectionObserver";

export const TestimonialAutoScrollSection = () => {
  const [headerRef, headerInView] = useIntersectionObserver();
  const [statsRef, statsInView] = useIntersectionObserver();

  // Pick one testimonial to feature (e.g., the first one with a highlight)
  const featuredTestimonial = enhancedTestimonials[0];

  return (
    <section id="testimoni" className="py-20 bg-gray-50 dark:bg-[#121212] overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        {/* Heading */}
        <div
          ref={headerRef}
          className={`max-w-3xl mx-auto text-center space-y-4 transition-all duration-700 ${
            headerInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#D32F2F]/10 text-[#D32F2F] text-sm font-medium mb-2">
            <Star className="w-4 h-4 fill-current" />
            <span>Trusted by 50+ Clients</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white">
            Apa Kata Mereka Tentang Kami
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            Fokus kami adalah memberikan dampak nyata bagi bisnis Anda.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 mb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
          {/* Featured Testimonial Card */}
          <div className="relative group perspective-1000">
            <div className="absolute -inset-1 bg-gradient-to-r from-[#D32F2F] to-orange-500 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
            <div className="relative bg-white dark:bg-[#1a1a1a] rounded-2xl p-8 sm:p-10 shadow-xl border border-gray-100 dark:border-gray-800">
              <Quote className="w-12 h-12 text-[#D32F2F]/20 absolute top-8 right-8" />
              
              <div className="flex items-center gap-4 mb-6">
                <img 
                  src={featuredTestimonial.avatar} 
                  alt={featuredTestimonial.name}
                  className="w-16 h-16 rounded-full border-2 border-[#D32F2F]/20 p-1"
                />
                <div>
                  <h3 className="font-bold text-xl text-gray-900 dark:text-white">
                    {featuredTestimonial.name}
                  </h3>
                  <p className="text-[#D32F2F] font-medium text-sm">
                    {featuredTestimonial.position}
                  </p>
                </div>
              </div>

              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-[#ecc94b] text-[#ecc94b]" />
                ))}
              </div>

              <blockquote className="text-xl sm:text-2xl font-medium text-gray-800 dark:text-gray-200 leading-relaxed mb-6">
                "{featuredTestimonial.text}"
              </blockquote>

              <div className="inline-block bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 px-4 py-2 rounded-lg text-sm font-semibold">
                🔥 {featuredTestimonial.highlight}
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div 
            ref={statsRef}
            className={`grid grid-cols-2 gap-6 transition-all duration-1000 delay-300 ${
              statsInView ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"
            }`}
          >
            <div className="bg-white dark:bg-[#1a1a1a] p-6 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-md transition-all">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center mb-4 text-blue-600 dark:text-blue-400">
                <Smile className="w-6 h-6" />
              </div>
              <h4 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">98%</h4>
              <p className="text-gray-500 text-sm">Client Satisfaction</p>
            </div>

            <div className="bg-white dark:bg-[#1a1a1a] p-6 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-md transition-all">
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center mb-4 text-green-600 dark:text-green-400">
                <Briefcase className="w-6 h-6" />
              </div>
              <h4 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">100+</h4>
              <p className="text-gray-500 text-sm">Projects Completed</p>
            </div>

            <div className="bg-white dark:bg-[#1a1a1a] p-6 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-md transition-all">
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center mb-4 text-purple-600 dark:text-purple-400">
                <Award className="w-6 h-6" />
              </div>
              <h4 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">5+</h4>
              <p className="text-gray-500 text-sm">Years Experience</p>
            </div>

            <div className="bg-white dark:bg-[#1a1a1a] p-6 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-md transition-all">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-xl flex items-center justify-center mb-4 text-red-600 dark:text-red-400">
                <Users className="w-6 h-6" />
              </div>
              <h4 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">24/7</h4>
              <p className="text-gray-500 text-sm">Support Team</p>
            </div>
          </div>
        </div>
      </div>

      {/* Auto Scroll Wrapper */}
      <div className="relative py-10">
        {/* Gradient Fade Left */}
        <div className="absolute left-0 top-0 w-24 h-full bg-gradient-to-r from-gray-50 dark:from-[#121212] to-transparent z-10" />

        {/* Gradient Fade Right */}
        <div className="absolute right-0 top-0 w-24 h-full bg-gradient-to-l from-gray-50 dark:from-[#121212] to-transparent z-10" />

        {/* Scroll Track */}
        <div 
          role="region" 
          aria-label="Client testimonials carousel" 
          className="flex gap-6 w-max animate-infinite-scroll hover:[animation-play-state:paused]"
        >
          {/* Duplicate enhanced testimonials for infinite effect */}
          {[...enhancedTestimonials, ...enhancedTestimonials].map((item, index) => (
            <div
              key={`${item.id}-${index}`}
              className="w-[350px] bg-white dark:bg-[#1a1a1a] border border-gray-100 dark:border-gray-800 rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300"
            >
              <div className="flex items-center gap-3 mb-4">
                 <img 
                  src={item.avatar} 
                  alt={item.name}
                  className="w-10 h-10 rounded-full bg-gray-100"
                  loading="lazy"
                />
                <div>
                   <h4 className="font-semibold text-gray-900 dark:text-white text-sm">{item.name}</h4>
                   <div className="flex items-center gap-1">
                     <p className="text-xs text-gray-500 dark:text-gray-400">{item.position}</p>
                   </div>
                </div>
              </div>

              <div className="flex gap-0.5 mb-3">
                {[...Array(item.rating)].map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-[#ecc94b] text-[#ecc94b]" />
                ))}
              </div>

              <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-sm mb-4 line-clamp-3">
                "{item.text}"
              </p>
              
              {item.highlight && (
                <span className="inline-block px-2 py-1 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 text-xs rounded font-medium">
                  {item.highlight}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
